import {
  FluidColorScheme,
  FluidIconConfig,
  FluidInputType,
  FluidKeyedObject,
  FluidLinkTarget,
  FluidTheme,
  FluidThemeInterface,
  generateUniqueId,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Prop,
  State,
} from '@stencil/core';

import { GLOBAL_HTML_ATTRS } from '../../constants/constants';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { initializeAttrTunnel } from '../../utils/elements/html.utils';
import { Utils } from '../../utils/utils';
import {
  FluidLinkTranslationOptions,
  LinkIcon,
  LinkText,
} from './components/fluid-link.components';

/**
 * @displayName Link
 *
 * @contextData FLUID Link is a navigational element used to direct users to a URL, section of a page, or file download. Links can appear inline within sentences or paragraphs, or on their own as standalone elements.
 * <br/><br/>
 * NOTE: Link supports the following native HTML attributes: "download", "rel", "ping", "referrerpolicy", "tabindex", "title", & "role". When applied to the root "fluid-link" element, these attributes will be "tunneled" through the component's shadow root & applied to the underlying anchor tag.
 *
 * @slot - Text / content displayed within the Link's <a> tag
 *
 * @builder true
 */
@Component({
  tag: 'fluid-link',
  styleUrl: 'fluid-link.scss',
  shadow: true,
})
export class FluidLink implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Own Properties (Private)
  /**
   * Array of supported native HTML attributes that can be tunneled from
   * the "host" element to the root <a> tag.
   */
  private _supportedHtmlAttrs: string[] = [
    ...GLOBAL_HTML_ATTRS,
    'download',
    'ping',
    'referrerpolicy',
    'rel',
  ];

  // ================================================================ //
  // -- Host Element

  /**
   * Get a reference to the host element
   */
  @Element() host: HTMLFluidLinkElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidLinkTranslationOptions;
  private _translateId: string;
  private _i18nStore: FluidI18nStore | undefined;

  // ================================================================ //
  // -- States

  /**
   * Object containing key/value pairs for each native HTML attribute
   * that has been "tunneled" from "host" element to inner <a> tag.
   * Used/updated by "initializeAttrTunnel()" method.
   */
  @State() _attrMap: FluidKeyedObject<string> = {};

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * NOTE: use this Prop INSTEAD OF "route" when Link should be a
   * standard <a> element (anchor tag) & navigation should NOT be
   * handled by the consuming application's router. Sets the URL to
   * navigate to when the Link is clicked.
   */
  @Prop({ mutable: true }) href: string;

  /**
   * NOTE: use this Prop INSTEAD OF "href" when navigation should be
   * handled by the consuming application's router (i.e. equivalent to
   * Angular's "routerLink" or React's <Link to="...">). Sets the route
   * to be emitted via the "linkClicked" Event when the Link is clicked.
   */
  @Prop({ mutable: true }) route: string;

  /**
   * When true, Link will be styled as an inline element with regular
   * font weight & an underline. When false (default), Link will be
   * styled as a "standalone" element (i.e. display: flex;) with bold
   * font weight & no underline (except on :hover). Inline styles
   * should be used for Links within text blocks or Tables.
   */
  @Prop({ mutable: true }) inline: boolean;

  /**
   * When true, Link will appear center-aligned within its container.
   * NOTE: this Prop cannot be used when "inline" Prop is true.
   */
  @Prop({ mutable: true }) center: boolean;

  /**
   * Specifies where to open the Link. "_blank" opens the link in a new
   * window or tab. "_self" opens the link in the same frame as it was
   * clicked (default). "_parent" opens the link in the parent frame.
   * "_top" opens the link in the full body of the window.
   */
  @Prop({ mutable: true }) target: FluidLinkTarget = FluidLinkTarget.SELF;

  /**
   * When true, Link will be disabled.
   */
  @Prop({ mutable: true }) disabled: boolean;

  /**
   * If Link is an email address or phone number, specifies the Link's
   * behavior when clicked. "email" will add a "mailto:" prefix to the
   * Link's "href" attribute, & will open the user's native email client
   * when clicked. "tel" will add a "tel:" prefix to the Link's "href"
   * attribute, & will open the user's native phone or VoIP application
   * when clicked. NOTE: this Prop MUST be used with the "href" Prop
   * (NOT the "route" Prop).
   */
  @Prop({ mutable: true }) type: Extract<
    FluidInputType,
    FluidInputType.EMAIL | FluidInputType.TEL
  >;

  /**
   * When provided, Link will render a FLUID Icon to the LEFT of its
   * slotted content.
   */
  @Prop({ mutable: true }) iconBefore: FluidIconConfig;

  /**
   * When provided, Link will render a FLUID Icon to the RIGHT of its
   * slotted content.
   */
  @Prop({ mutable: true }) iconAfter: FluidIconConfig;

  /**
   * Sets a Link's text color
   * to "light" or "dark" for increased contrast when displayed on a color
   * background. For example, if your Link is displayed on a DARK
   * BLUE background, set this Prop's value to "light" so that the text
   * color will be white (instead of the default blue). If your
   * Link is displayed on a YELLOW background, set this Prop's value to
   * "dark" so that the label will be dark gray (instead of the default
   * blue). Do NOT change this Prop's value if your Link is
   * displayed on a WHITE background.
   */
  @Prop({ mutable: true }) colorScheme: FluidColorScheme =
    FluidColorScheme.DEFAULT;

  // ================================================================ //
  // -- Events

  /**
   * Emits "route" or "href" Prop value when Link is clicked.
   */
  @Event() linkClicked: EventEmitter<string>;

  // ================================================================ //
  // -- Testing Enablement

  /**
   * When supplied, will be prepended on each inner element of the link component in order
   * to help facilitate
   */
  @Prop({ mutable: true }) identifier: string;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   * @action: mapDeprecatedProps - maps "link" Prop value to "href" or "route"
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    useI18n.bind(this)();
    this.mapDeprecatedProps();
    this.identifier = this.identifier ?? this.host.id ?? generateUniqueId();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action: mapDeprecatedProps - maps deprecated Prop values to new/replacement Props
   * @action: initializeAttrTunnel - tunnels native HTML attrs from "host" element to <a> tag
   */
  componentWillLoad() {
    this.mapDeprecatedProps();
    initializeAttrTunnel(this, this._supportedHtmlAttrs);
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Maps deprecated "link" Prop value to new "href" or "route" Props
   * depending on the value of deprecated "useNative" Prop. If "useNative"
   * is TRUE, Link should be a standard anchor tag & "link" Prop value
   * will be mapped to "href" Prop. If "useNative" is FALSE, navigation
   * will be handled by consuming application's router & "link" Prop
   * value will be mapped to "route" Prop.
   */
  mapDeprecatedProps(): void {
    switch (true) {
      case !this.link:
        return;
      case this.useNative:
        this.href = this.link;
        break;
      case !this.useNative:
        this.route = this.link;
        break;
    }
  }

  /**
   * If "type" Prop is provided, returns the correct "href" prefix so
   * that Link is handled by appropriate application when clicked. If
   * "type" Prop is NOT set, simply returns "href" attr "as is" (if
   * "href" IS defined) OR returns "#" if "href" is NOT defined so that
   * Link is valid.
   */
  getHref(): string {
    if (this.route) return this.route;
    switch (this.type) {
      case undefined:
        return this.href ?? this.link ?? '#';
      case FluidInputType.EMAIL:
        return `mailto:${this.href}`;
      case FluidInputType.TEL:
        return `tel:${this.href}`;
    }
  }

  /**
   * Emits Link's "route" or "href" Prop value when clicked. Also
   * prevents default <a> tag behavior when: "route" Prop is used
   * (necessary for routing), "route" AND "href" Props are undefined,
   * or Link is disabled.
   * @param event - click event that occurred on Link's <a> tag
   */
  handleClick(event: MouseEvent): void {
    (!!this.route || !this.href || this.disabled) && event.preventDefault();
    !this.disabled && this.linkClicked.emit(this.route ?? this.href);
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <Host
        class={{
          'fluid-link--inline': this.inline,
          'fluid-link--center': this.center,
        }}
        data-test-id={`${this.identifier}-fluid-link`}>
        <a
          tabIndex={this.disabled && -1}
          {...this._attrMap}
          href={this.getHref()}
          target={this.target}
          aria-disabled={String(!!this.disabled)}
          onClick={(event) => this.handleClick(event)}
          class={{
            [`fluid-link theme-${this.theme}`]: true,
            [`color-scheme-${this.colorScheme}`]: true,
            inline: this.inline,
            center: this.center,
            disabled: this.disabled,
          }}
          data-test-id={`${this.identifier}-fluid-link-anchor`}>
          <LinkIcon
            identifier={this.identifier}
            position={'before'}
            theme={this.theme}
            iconConfig={this.iconBefore}
            disabled={this.disabled}
          />
          <LinkText
            identifier={this.identifier}
            store={this._i18nStore}
            fluidTranslate={this.fluidTranslate}
          />
          <LinkIcon
            identifier={this.identifier}
            position={'after'}
            theme={this.theme}
            iconConfig={this.iconAfter}
            disabled={this.disabled}
          />
        </a>
      </Host>
    );
  }

  // ================================================================ //
  // -- Deprecated Props

  /**
   * DEPRECATED: "link" is now deprecated. Instead, use "href" Prop for
   * standard <a> tag functionality, or use "route" Prop for navigation
   * handled by the consuming application's router. -- The URL / route to
   * navigate to when the Link is clicked. When "useNative" Prop is TRUE,
   * this Prop's value must be a valid URL. When "useNative" Prop is FALSE,
   * navigation will NOT occur automatically & will need to be handled
   * via the consuming application's router.
   * @deprecated
   */
  @Prop({ mutable: true }) link: string;

  /**
   * DEPRECATED: "useNative" is now deprecated. Instead, use "href"
   * Prop for standard <a> tag functionality, or use "route" Prop for
   * navigation handled by the consuming application's router. -- When
   * true, Link will behave as a native HTML <a> tag and will direct the
   * browser to the configured link (i.e. via "href" attr). Set to false
   * to handle routing internally.
   * @deprecated
   */
  @Prop({ mutable: true }) useNative = true;
}
